import React, { useState } from 'react';
import { Row, Col } from 'antd';
import Link from 'next/link';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

interface LocationItemProps {
  imagePath: string;
  title: string;
  link: string;
  isTablet?: boolean;
  isMobile?: boolean;
}

const LocationItem: React.FC<LocationItemProps> = ({ imagePath, title, link, isTablet, isMobile }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Link href={link} passHref>
      <div
        style={{
          position: 'relative',
          height: '100%',
          borderRadius: '10px',
          overflow: 'hidden',
          cursor: 'pointer',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            position: 'relative',
            aspectRatio: '4/3',
          }}
        >
          <Image
            src={imagePath}
            alt={title}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            style={{
              objectFit: 'cover',
              borderRadius: '10px',
              transform: isHovered ? 'scale(1.1)' : 'scale(1.0)',
              transition: 'transform 0.4s ease-in-out',
            }}
          />
        </div>
        <div
          style={{
            position: 'absolute',
            bottom: '0',
            left: '0',
            background: '#DE6B2699',
            color: 'white',
            padding: isMobile ? '12px 24px' : (isTablet ? '16px 32px' : '13.5px 32px'),
            borderRadius: isMobile ? '0' : '10px',
            fontSize: isMobile ? '23px' : (isTablet ? '23px' : '28px'),
            fontWeight: 500,
            zIndex: 2,
            width: isMobile ? '100%' : 'auto',
            textAlign: isMobile ? 'center' : 'left',
            boxSizing: 'border-box',
          }}
        >
          {title}
        </div>
      </div>
    </Link>
  );
};

interface PropertyLocationSectionProps {
  isTablet?: boolean;
  isMobile?: boolean;
}

const PropertyLocationSection: React.FC<PropertyLocationSectionProps> = ({ isTablet, isMobile }) => {
  const { t } = useTranslation('home');

  return (
    <div style={{ backgroundColor: '#F5F5F6' }}>
      <div
        style={{
          maxWidth: '1496px',
          margin: '0px auto',
          padding: isMobile ? '48px 16px' : (isTablet ? '64px 57px' : '96px 0px'),
        }}
      >
        <p style={{ 
          marginBottom: isMobile ? '24px' : '40px', 
          fontSize: isMobile ? '28px' : (isTablet ? '33px' : '40px'), 
          fontWeight: 500 
        }}>
          {t('property_locations.title')}
        </p>

        {/* First row - Mobile: 100% each, Tablet: 431px + 281px, Desktop: 70% + 30% */}
        <Row gutter={[8, 8]} style={{ marginBottom: '8px' }}>
          <Col 
            xs={24} // 100% on mobile
            sm={24} // 100% on small screens
            md={isTablet ? 14 : 17} // tablet and desktop spans
            style={{ height: 'auto' }} // ใช้ auto height เพื่อให้ aspect ratio ทำงาน
          >
            <LocationItem
              imagePath="/assets/homePage/lifestyle/bts_mrt.webp"
              title={t('property_locations.bts_mrt')}
              link="/location/bts-mrt"
              isTablet={isTablet}
              isMobile={isMobile}
            />
          </Col>
          <Col 
            xs={24} // 100% on mobile
            sm={24} // 100% on small screens
            md={isTablet ? 10 : 7} // tablet and desktop spans
            style={{ height: 'auto' }} // ใช้ auto height เพื่อให้ aspect ratio ทำงาน
          >
            <LocationItem
              imagePath="/assets/homePage/lifestyle/hospital.webp"
              title={t('property_locations.hospital')}
              link="/location/hospital"
              isTablet={isTablet}
              isMobile={isMobile}
            />
          </Col>
        </Row>

        {/* Second row - Mobile: 100% each, Tablet/Desktop: 33% + 33% + 33% */}
        <Row gutter={[8, 8]} style={{ marginBottom: '8px' }}>
          <Col 
            xs={24} // 100% on mobile
            sm={24} // 100% on small screens
            md={8} // 33% on tablet and desktop
            style={{ height: 'auto' }} // ใช้ auto height เพื่อให้ aspect ratio ทำงาน
          >
            <LocationItem
              imagePath="/assets/homePage/lifestyle/main_road.webp"
              title={t('property_locations.main_road')}
              link="/location/main-road"
              isTablet={isTablet}
              isMobile={isMobile}
            />
          </Col>
          <Col 
            xs={24} // 100% on mobile
            sm={24} // 100% on small screens
            md={8} // 33% on tablet and desktop
            style={{ height: 'auto' }} // ใช้ auto height เพื่อให้ aspect ratio ทำงาน
          >
            <LocationItem
              imagePath="/assets/homePage/lifestyle/university.webp"
              title={t('property_locations.university')}
              link="/location/university"
              isTablet={isTablet}
              isMobile={isMobile}
            />
          </Col>
          <Col 
            xs={24} // 100% on mobile
            sm={24} // 100% on small screens
            md={8} // 33% on tablet and desktop
            style={{ height: 'auto' }} // ใช้ auto height เพื่อให้ aspect ratio ทำงาน
          >
            <LocationItem
              imagePath="/assets/homePage/lifestyle/shopping_mall.webp"
              title={t('property_locations.shopping_mall')}
              link="/location/shopping-mall"
              isTablet={isTablet}
              isMobile={isMobile}
            />
          </Col>
        </Row>

        {/* Third row - Mobile: 100% each, Tablet: 431px + 281px, Desktop: 70% + 30% */}
        <Row gutter={[8, 8]}>
          <Col 
            xs={24} // 100% on mobile
            sm={24} // 100% on small screens
            md={isTablet ? 14 : 17} // tablet and desktop spans
            style={{ height: 'auto' }} // ใช้ auto height เพื่อให้ aspect ratio ทำงาน
          >
            <LocationItem
              imagePath="/assets/homePage/lifestyle/airport.webp"
              title={t('property_locations.airport')}
              link="/location/airport"
              isTablet={isTablet}
              isMobile={isMobile}
            />
          </Col>
          <Col 
            xs={24} // 100% on mobile
            sm={24} // 100% on small screens
            md={isTablet ? 10 : 7} // tablet and desktop spans
            style={{ height: 'auto' }} // ใช้ auto height เพื่อให้ aspect ratio ทำงาน
          >
            <LocationItem
              imagePath="/assets/homePage/lifestyle/expressway.webp"
              title={t('property_locations.expressway')}
              link="/location/expressway"
              isTablet={isTablet}
              isMobile={isMobile}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default PropertyLocationSection; 